import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PhotoTrigger } from './photo-viewer.tsx';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  customerApi,
  messagesApi,
  type ConversationMessage,
  type ConversationSummary,
  type MessageAttachment,
} from '../api/endpoints.ts';
import { Alert, Button, ConfirmDialog, EmptyState, LoadingBlock } from './ui.tsx';
import { MessageComposer, type ReplyContext } from './message-composer.tsx';
import SearchSelect from './search-select.tsx';
import { INBOUND_EMAIL_ATTACHMENT_MAX_BYTES } from '../../shared/constants/index.ts';
import { usePlanFeature } from './plan-gate.tsx';
import { InboxVisitInstruction } from './inbox-visit-instruction.tsx';
import { useIsPublicDemo } from './demo-limit-note.tsx';
import {
  CallDetails,
  conversationPreviewText,
  isAnswerableInbound,
  replyChannelFor,
  unplacedCallLabel,
} from './call-details.tsx';

/**
 * Everything the company has said to this customer, and everything they have
 * said back, in one list.
 *
 * The value is entirely in it being ONE list. An office that has to look in
 * two places to find out what has been said will check one of them, and the
 * reminder goes out twice from two people who each read a different half.
 *
 * Channel is shown as a word and an icon rather than only as a color, so the
 * distinction survives a color-blind reader, a grayscale print and a
 * high-contrast theme.
 */

const CHANNEL_LABEL: Record<string, string> = {
  email: 'Email',
  sms: 'Text',
  internal: 'Internal note',
};

const CHANNEL_ICON: Record<string, string> = {
  email: '✉️',
  sms: '💬',
  internal: '📝',
  call: '☎',
};

type Filter = 'all' | 'email' | 'sms';

/** How much of a thread loads at first, and how much each "older" click adds. */
const THREAD_PAGE = 50;

export function ConversationTimeline({ customerId }: { customerId: string }) {
  const isPublicDemo = useIsPublicDemo();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<Filter>('all');

  // Asked for by customer, not found by paging the inbox: a company with more
  // conversations than fit on a page would otherwise be told this customer has
  // never been written to, which is both wrong and worse the busier they get.
  const conversations = useQuery({
    queryKey: ['conversations', 'customer', customerId],
    queryFn: () => messagesApi.conversations({ customerId, pageSize: 1 }),
  });

  const conversation = conversations.data?.items[0];

  /**
   * How much of the history is on screen.
   *
   * The server always serves the RECENT end of the thread, so a fixed page
   * would silently cut the BEGINNING of a long relationship — the part
   * somebody scrolls back for. So the page grows on request instead.
   */
  const [shown, setShown] = useState(THREAD_PAGE);

  const thread = useQuery({
    queryKey: ['conversation-thread', conversation?.id, shown],
    queryFn: () => messagesApi.thread(conversation!.id, { pageSize: shown }),
    enabled: Boolean(conversation),
    placeholderData: (previous) => previous,
  });

  // A full page means there is probably more behind it. Inferred rather than
  // counted, because a count would be a second query on every open for a fact
  // only the button needs.
  const hasOlder = (thread.data?.messages.length ?? 0) >= shown;

  const markRead = useMutation({
    mutationFn: () => messagesApi.markRead(conversation!.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });

  /**
   * What the composer opens as, and what pressing Reply changes it to.
   *
   * `suggested` is the standing answer — the channel the customer last used
   * and, for an email, their subject — so the box is already right without
   * anybody touching it. `reply` is somebody choosing a particular message,
   * which overrides it. Both are the same shape, so the composer only knows
   * about one idea.
   */
  const replyNonce = useRef(0);
  const [reply, setReply] = useState<ReplyContext | null>(null);
  if (reply) replyNonce.current = reply.nonce;

  if (conversations.isLoading) return <LoadingBlock label="Loading messages" />;

  /**
   * The thread the way it is read: newest first.
   *
   * What somebody opening a customer wants is what was said last — an office
   * reading a two-year relationship top-down to find this morning's text is
   * reading the whole thing to reach the one line that mattered.
   *
   * Reversed rather than sorted on `createdAt`: two messages can carry the
   * same timestamp to the millisecond (a send and the note beside it), and the
   * server has already broken that tie by id. A sort here would undo it.
   */
  const ordered = [...(thread.data?.messages ?? [])].reverse();

  const messages = ordered.filter((message) => filter === 'all' || message.channel === filter);

  // The newest inbound, which is now simply the first one in the list.
  const lastInbound = ordered.find(isAnswerableInbound);

  const suggested: ReplyContext | undefined = lastInbound
    ? {
        channel: replyChannelFor(lastInbound),
        subject: lastInbound.subject,
        replyToAddress: lastInbound.replyToAddress,
        nonce: 0,
      }
    : undefined;

  return (
    <section className="card mb-6" aria-labelledby="conversation-heading">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 id="conversation-heading" className="text-lg font-semibold">
          Messages
        </h2>
        {conversation && conversation.unreadCount > 0 && !isPublicDemo ? (
          <Button variant="secondary" onClick={() => markRead.mutate()} busy={markRead.isPending}>
            Mark {conversation.unreadCount} read
          </Button>
        ) : null}
      </div>

      {/* Above the history rather than under it. The newest message is now the
          first row of the list, so the box sits beside the thing being
          answered — where under a newest-first thread it would be a hundred
          messages further down, past everything the reply is not about. */}
      <div className="mb-4 border-b border-[color:var(--color-line)] pb-4">
        <MessageComposer customerId={customerId} replyContext={reply ?? suggested} />
      </div>

      {/* Down here, against the list it acts on. Above the composer it sat
          directly over that box's own Email/Text/Note buttons — two rows of
          pills, one deciding what you are READING and one deciding what you
          are WRITING, and nothing but the wording to tell them apart. */}
      <div className="mb-3 flex flex-wrap gap-2" role="group" aria-label="Filter messages by channel">
        {(['all', 'email', 'sms'] as const).map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={filter === value}
            className={filter === value ? 'btn btn-primary' : 'btn btn-secondary'}
            onClick={() => setFilter(value)}
          >
            {value === 'all' ? 'All' : value === 'email' ? 'Email' : 'Text messages'}
          </button>
        ))}
      </div>

      {messages.length === 0 ? (
        <EmptyState title="Nothing yet">
          Emails and texts sent to this customer appear here, newest first — including the ones written above.
        </EmptyState>
      ) : (
        <>
          <ol className="mb-3 flex flex-col gap-3">
            {messages.map((message) => (
              <MessageRow
                key={message.id}
                message={message}
                customerId={customerId}
                onReply={
                  // Only what a customer actually said can be replied to. An
                  // internal note has nobody to answer, and answering your own
                  // outbound message is just writing a new one.
                  isAnswerableInbound(message)
                    ? () =>
                        setReply({
                          channel: replyChannelFor(message),
                          subject: message.subject,
                          // Whoever wrote it, not whoever the record names.
                          replyToAddress: message.replyToAddress,
                          nonce: replyNonce.current + 1,
                        })
                    : undefined
                }
              />
            ))}
          </ol>
          {/* Under the list, because the thread reads newest-first: what this
              loads is older than everything above it, and it arrives at the
              bottom where the reading has already got to. */}
          {hasOlder ? (
            <div className="mb-4">
              <Button
                variant="secondary"
                busy={thread.isFetching}
                onClick={() => setShown((current) => current + THREAD_PAGE)}
              >
                Load earlier messages
              </Button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

export function MessageRow({
  message,
  onReply,
  onUseDraft,
  customerId,
  localize = false,
}: {
  message: ConversationMessage;
  onReply?: () => void;
  /** Puts a voicemail's suggested reply into the composer. */
  onUseDraft?: (text: string) => void;
  customerId?: string | null;
  /** The Messages inbox uses its EN/ES labels without changing customer history. */
  localize?: boolean;
}) {
  const { t, i18n } = useTranslation();
  // The shared demo files replies without sending them, but its sample
  // conversations are otherwise read-only (shared/public-demo.ts), so
  // "Add to visit" cannot succeed there.
  const isPublicDemo = useIsPublicDemo();
  const outbound = message.direction === 'outbound';
  const internal = message.channel === 'internal';
  const call = message.channel === 'call';
  const answerable = isAnswerableInbound(message);

  return (
    <li
      className={[
        'rounded border p-3',
        internal
          ? 'border-dashed border-[color:var(--color-line)] bg-[color:var(--color-surface)]'
          : 'border-[color:var(--color-line)]',
      ].join(' ')}
    >
      <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-[color:var(--color-muted)]">
        <span aria-hidden="true">{CHANNEL_ICON[message.channel] ?? '•'}</span>
        {/* The word as well as the icon and the color — an icon alone is not
            a label, and a color alone is not information. */}
        <span className="font-medium">
          {call
            ? t(outbound ? 'voice.outgoingCall' : 'voice.incomingCall')
            : localize
              ? t(`messageComposer.channel.${message.channel}`, { defaultValue: message.channel })
              : (CHANNEL_LABEL[message.channel] ?? message.channel)}
        </span>
        {/* Who, where the app knows. A text carries only the company label in
            its body, so without the name a shared inbox shows four people's
            messages as one anonymous voice — and the office goes back to
            asking each other who already spoke to the customer. Anything a
            cron composed has no name and simply says "sent". */}
        {/* A call the office placed says who placed it, as a text says who sent it. */}
        {call && outbound && message.senderName ? (
          <span>{t('voice.calledBy', { name: message.senderName })}</span>
        ) : null}
        {!call ? (
          <span>
            {localize
              ? outbound
                ? message.senderName
                  ? t('messagesInbox.sentBy', { name: message.senderName })
                  : t('messagesInbox.delivery.sent')
                : t('messagesInbox.received')
              : outbound
                ? message.senderName
                  ? `sent by ${message.senderName}`
                  : 'sent'
                : 'received'}
          </span>
        ) : null}
        <time dateTime={message.createdAt}>
          {new Date(message.createdAt).toLocaleString(localize ? i18n.language : undefined)}
        </time>
        {!internal && !call ? <DeliveryState message={message} localize={localize} /> : null}
        {onReply && (!call || answerable) ? (
          <button type="button" className="ml-auto underline" onClick={onReply}>
            {localize
              ? t(replyChannelFor(message) === 'sms' ? 'messagesInbox.replyText' : 'messagesInbox.replyEmail')
              : `Reply by ${replyChannelFor(message) === 'sms' ? 'text' : 'email'}`}
          </button>
        ) : null}
      </div>

      {message.subject ? (
        <p className="max-w-full min-w-0 overflow-x-auto text-sm font-medium">{message.subject}</p>
      ) : null}
      {call && message.call ? (
        <CallDetails message={message} {...(onUseDraft ? { onUseDraft } : {})} />
      ) : call && outbound ? (
        <p className="text-sm">{unplacedCallLabel(message, t)}</p>
      ) : (
        <p className="max-w-full min-w-0 overflow-x-auto text-sm whitespace-pre-wrap">{message.body}</p>
      )}
      {message.attachments.length > 0 ? <Attachments attachments={message.attachments} /> : null}
      {customerId && !isPublicDemo && answerable ? (
        <InboxVisitInstruction customerId={customerId} messageId={message.id} />
      ) : null}
    </li>
  );
}

/**
 * What a customer sent along with their words.
 *
 * The one that did not fit is listed too, grayed and unclickable with its size
 * shown. A photo silently missing from a reply reads as a customer who never
 * sent one — and somebody calls to ask for a picture that was already sent
 * once. Saying "too large" is what lets them ask for a smaller one instead.
 */
function Attachments({ attachments }: { attachments: MessageAttachment[] }) {
  const group = useId();
  return (
    <ul className="mt-2 flex flex-wrap gap-2">
      {attachments.map((attachment) => (
        <li key={attachment.id}>
          {attachment.available && attachment.mimeType.startsWith('image/') ? (
            <PhotoTrigger
              src={messagesApi.attachmentUrl(attachment.id)}
              alt={attachment.filename}
              filename={attachment.filename}
              group={group}
              className="inline-flex max-w-full min-w-0 items-center gap-1 rounded border border-[color:var(--color-line)] px-2 py-1 text-xs underline"
            >
              <span aria-hidden="true">📎</span>
              <span className="max-w-full min-w-0 overflow-x-auto">{attachment.filename}</span>
              <span className="text-[color:var(--color-muted)]">{formatSize(attachment.sizeBytes)}</span>
            </PhotoTrigger>
          ) : attachment.available ? (
            <a
              className="inline-flex max-w-full min-w-0 items-center gap-1 rounded border border-[color:var(--color-line)] px-2 py-1 text-xs underline"
              href={messagesApi.attachmentUrl(attachment.id)}
              // The Worker sets `Content-Disposition: attachment`; this names
              // the file for the browser that honors the hint instead.
              download={attachment.filename}
            >
              <span aria-hidden="true">📎</span>
              <span className="max-w-full min-w-0 overflow-x-auto">{attachment.filename}</span>
              <span className="text-[color:var(--color-muted)]">{formatSize(attachment.sizeBytes)}</span>
            </a>
          ) : (
            <span
              className="inline-flex max-w-full min-w-0 items-center gap-1 rounded border border-dashed border-[color:var(--color-line)] px-2 py-1 text-xs text-[color:var(--color-muted)]"
              title={unavailableReason(attachment)}
            >
              <span aria-hidden="true">📎</span>
              <span className="max-w-full min-w-0 overflow-x-auto">{attachment.filename}</span>
              <span>
                ({formatSize(attachment.sizeBytes)}, {unavailableReason(attachment)})
              </span>
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * Why a file is not there to open.
 *
 * A file that was over the limit and a file whose upload failed both arrive
 * with no storage key, and telling somebody a 40 KB photo was "too large" sends
 * them to ask the customer for a smaller one that would fail the same way. The
 * size says which happened — nothing else has to be stored to know.
 */
function unavailableReason(attachment: MessageAttachment): string {
  return attachment.sizeBytes > INBOUND_EMAIL_ATTACHMENT_MAX_BYTES ? 'too large to keep' : 'could not be saved';
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Sent is not delivered, and the difference is worth showing.
 *
 * A provider accepting a text is not a handset receiving it. An office told
 * only "sent" who then hears nothing has no idea the message bounced twenty
 * seconds later — so the two states are distinct here, and the failure states
 * say what to do rather than quoting a provider.
 */
function DeliveryState({ message, localize = false }: { message: ConversationMessage; localize?: boolean }) {
  const { t } = useTranslation();
  const label = (key: string, fallback: string) => (localize ? t(`messagesInbox.delivery.${key}`) : fallback);
  switch (message.status) {
    case 'delivered':
      return <span aria-label={label('delivered', 'Delivered')}>✓✓ {label('delivered', 'Delivered')}</span>;
    case 'sent':
      return <span aria-label={label('sent', 'Sent')}>✓ {label('sent', 'Sent')}</span>;
    case 'undelivered':
    case 'failed':
      return <span className="text-[color:var(--color-danger)]">! {label('failed', 'Could not be delivered')}</span>;
    case 'unknown':
      // The ambiguous outcome, said plainly. Somebody has to decide whether to
      // re-send, and the app deliberately will not decide for them.
      return <span className="text-[color:var(--color-danger)]">? {label('unknown', 'Delivery unconfirmed')}</span>;
    case 'suppressed':
      return <span>{label('suppressed', 'Not sent')}</span>;
    case 'received':
      return null;
    default:
      return <span>○ {label('queued', 'Queued')}</span>;
  }
}

/**
 * The unresolved inbox: texts from numbers nobody recognized.
 *
 * Its own list rather than a badge on a customer, because the whole point of
 * refusing to guess is that a human decides who these are from.
 */
export function UnknownSendersInbox() {
  // The customers page is open on every plan. This list is the one request
  // on it that the messaging gate would refuse, so Free never asks.
  if (!usePlanFeature('messaging')) return null;
  return <UnknownSendersInboxList />;
}

function UnknownSendersInboxList() {
  // Asked for as its own filtered list rather than sifted out of a page of the
  // inbox: an unresolved text is exactly the row a busy company's first page
  // pushes off the end, and it is the one somebody has to see.
  const conversations = useQuery({
    queryKey: ['conversations', 'unresolved'],
    queryFn: () => messagesApi.conversations({ unresolved: true, pageSize: 25 }),
  });

  const unresolved = conversations.data?.items ?? [];
  if (unresolved.length === 0) return null;

  return (
    <section className="card mb-6" aria-labelledby="unknown-senders-heading">
      <h2 id="unknown-senders-heading" className="mb-1 text-lg font-semibold">
        Texts from numbers we do not recognize
      </h2>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">
        Nobody is guessed at. Choose who each of these is from and it joins that customer&rsquo;s history — a reply then
        goes back to the right person, with the right context in front of whoever writes it.
      </p>

      <ul className="flex flex-col gap-4">
        {unresolved.map((conversation) => (
          <UnknownSenderRow key={conversation.id} conversation={conversation} />
        ))}
      </ul>
    </section>
  );
}

/** One stranger's thread, and the act of deciding who they are. */
function UnknownSenderRow({ conversation }: { conversation: ConversationSummary }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmDismiss, setConfirmDismiss] = useState(false);

  const matches = useQuery({
    queryKey: ['customers', 'attach-picker', search],
    queryFn: () => customerApi.list({ q: search, page: 1, pageSize: 20 }),
  });

  const attach = useMutation({
    mutationFn: () => messagesApi.attach(conversation.id, customerId),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: () => {
      // The usual cause is the customer already having a thread of their own;
      // merging two histories is a different operation with its own decisions.
      setError('That could not be attached — the customer may already have a conversation.');
    },
  });

  const dismiss = useMutation({
    mutationFn: () => messagesApi.dismiss(conversation.id, conversation.lastMessageAt),
    onSuccess: async () => {
      setConfirmDismiss(false);
      await queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    // Refused because newer activity arrived, or somebody else handled it.
    // Reload the row so the person sees what changed and a retry carries the
    // current version instead of the stale one.
    onError: () => queryClient.invalidateQueries({ queryKey: ['conversations'] }),
  });

  return (
    <li className="rounded border border-[color:var(--color-line)] p-4">
      <p className="font-medium">{conversationPreviewText(conversation, t) ?? 'A text with no readable body'}</p>
      <p className="mb-3 text-sm text-[color:var(--color-muted)]">
        {conversation.lastInboundAt ? new Date(conversation.lastInboundAt).toLocaleString() : 'Recently'}
      </p>

      {error ? (
        <div className="mb-3">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-64 flex-1">
          <SearchSelect
            label="Attach to"
            hint="Type a name, email or phone to search."
            placeholder="Choose a customer…"
            value={customerId}
            onChange={setCustomerId}
            onSearch={setSearch}
            loading={matches.isFetching}
            errored={matches.isError}
            options={(matches.data?.items ?? []).map((customer) => ({
              value: customer.id,
              label: `${customer.firstName} ${customer.lastName}`.trim() || customer.email || customer.id,
              description: customer.email ?? undefined,
            }))}
          />
        </div>
        <Button variant="primary" disabled={customerId === ''} busy={attach.isPending} onClick={() => attach.mutate()}>
          Attach
        </Button>
        <Button variant="secondary" onClick={() => setConfirmDismiss(true)}>
          Dismiss
        </Button>
      </div>

      {confirmDismiss ? (
        <ConfirmDialog
          title="Dismiss this conversation?"
          confirmLabel="Dismiss"
          confirmVariant="danger"
          busy={dismiss.isPending}
          error={
            dismiss.isError
              ? 'That could not be dismissed. It may have been handled, or a new message may have arrived.'
              : null
          }
          onConfirm={() => {
            if (!dismiss.isPending) dismiss.mutate();
          }}
          onCancel={() => setConfirmDismiss(false)}
        >
          <p>
            It will leave this list and not be attached to any customer. If that number texts or calls again, it comes
            back here.
          </p>
        </ConfirmDialog>
      ) : null}
    </li>
  );
}
